import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import NationNav from "@/components/NationNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, Plus, Users, ChevronRight, Trash2, Archive, ArchiveRestore } from "lucide-react";

export default function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", season: "", division: "" });
  const [filter, setFilter] = useState("active");

  const load = () => api.get("/admin/teams", { params: { status_filter: filter } }).then(({ data }) => setTeams(data.items || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const del = async (t) => {
    if (!window.confirm(`Delete team "${t.name}"? This permanently removes the team and its roster entries. Use Archive to retire a past-season team without losing history.`)) return;
    try {
      await api.delete(`/admin/teams/${t.id}`);
      toast.success("Team deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const setArchived = async (t, archived) => {
    try {
      await api.patch(`/admin/teams/${t.id}`, { status: archived ? "archived" : "active" });
      toast.success(archived ? "Team archived" : "Team restored");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Team name required"); return; }
    try {
      await api.post("/admin/teams", form);
      toast.success("Team created");
      setForm({ name: "", season: "", division: "" });
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Shield className="w-4 h-4" /> CBR Nation</div>
        <div className="flex items-end justify-between">
          <div>
            <h1 data-testid="teams-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Teams</h1>
            <p className="text-xs text-slate-500 mt-1">Create CBR Nation teams and manage their rosters from selection to confirmation.</p>
          </div>
          <button data-testid="new-team-btn" onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
            <Plus className="w-4 h-4" /> New Team
          </button>
        </div>

        <NationNav />

        <div className="flex gap-1 mt-6" data-testid="teams-filter">
          {[["active", "Active"], ["archived", "Archived"], ["all", "All"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`teams-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm ${filter === k ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#0A192F]"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {showForm && (
          <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5 grid sm:grid-cols-4 gap-3 items-end">
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="label-overline text-[10px]">Team Name</span>
              <input data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="CBR Nation 2028" className="border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-overline text-[10px]">Season</span>
              <input data-testid="team-season-input" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="2028" className="border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-overline text-[10px]">Division</span>
              <input data-testid="team-division-input" value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} placeholder="Junior / Senior / Red / Black" className="border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            </label>
            <button data-testid="create-team-submit" onClick={create} className="sm:col-span-4 self-start bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">Create Team</button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {teams.map((t) => (
            <div key={t.id} className="relative group bg-white border rounded-sm overflow-hidden transition-colors flex flex-col" style={{}}>
              <Link to={`/admin/teams/${t.id}`} data-testid={`team-card-${t.id}`} className={`block p-5 border-b transition-colors ${t.status === "archived" ? "border-slate-100 opacity-70 hover:opacity-100" : "border-slate-100 hover:bg-slate-50"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl uppercase tracking-tight pr-2">{t.name}</span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                  <span>{[t.season, t.division].filter(Boolean).join(" · ") || "—"}</span>
                  {t.status === "archived" && <span className="text-[9px] font-semibold uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-sm">Archived</span>}
                </div>
                <div className="flex items-center gap-4 mt-4 text-xs">
                  <span className="inline-flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5" /> {t.roster_counts?.total ?? 0} players</span>
                  <span className="text-emerald-700">{t.roster_counts?.confirmed ?? 0} confirmed</span>
                  <span className="text-amber-700">{t.roster_counts?.invited ?? 0} invited</span>
                </div>
              </Link>
              <div className="flex items-stretch divide-x divide-slate-100 bg-slate-50/50">
                <Link to={`/admin/teams/${t.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#0A192F] hover:bg-white transition-colors">
                  <Users className="w-3.5 h-3.5" /> Manage
                </Link>
                {t.status === "archived" ? (
                  <button data-testid={`restore-team-${t.id}`} onClick={() => setArchived(t, false)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 hover:text-white hover:bg-emerald-600 transition-colors">
                    <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                  </button>
                ) : (
                  <button data-testid={`archive-team-${t.id}`} onClick={() => setArchived(t, true)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-white hover:bg-[#0A192F] transition-colors">
                    <Archive className="w-3.5 h-3.5" /> Archive
                  </button>
                )}
                <button data-testid={`delete-team-${t.id}`} onClick={() => del(t)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-[#E63946] hover:text-white hover:bg-[#E63946] transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
          {teams.length === 0 && <div className="sm:col-span-2 lg:col-span-3 text-sm text-slate-500 bg-white border border-slate-200 rounded-sm p-10 text-center">No {filter === "all" ? "" : filter} teams.</div>}
        </div>
      </div>
    </AdminLayout>
  );
}
